/**
 * Procurement Planning Helpers
 * Pure functions for procurement data transformations and display logic.
 */

import { fetchIngredients } from '@/lib/api/ingredients';
import type {
  AddManualProcurementItemRequest,
  BulkInventoryEntry,
  Ingredient,
  IngredientCategory,
  ProcurementBreakdownEntry,
  ProcurementItem,
  UpdateProcurementItemRequest,
} from '@/types/api';
import { INGREDIENT_CATEGORY_LABELS, UNASSIGNED_VENDOR_LABEL } from '@/types/api';
import {
  DEFAULT_RSVP_SIZE_SCALE_RATIOS,
  MEDIUM_TO_LARGE_RATIO,
  QUANTITY_DECIMAL_PLACES,
  RSVP_SIZES,
  createRsvpSizeCounts,
  convertOrderIncrementToUnit,
  getAutoConversionMultiplier,
  netStapleOnHand,
  resolveInventoryUnitOnSave,
  resolveSharedInventoryUnit,
  roundQuantity,
  roundUpToIncrement,
  type RSVPScaleRatios,
  type RSVPSizeCode,
  type RSVPSizeCounts,
} from '@/types/units';
import type {
  ProcurementDisplayItem,
  ProcurementGroup,
  ProcurementItemDraft,
  ProcurementViewMode,
} from './types';
import type { ProcurementExportGroup } from '@/lib/utils/procurementExportWorkbook';

export function formatTimestamp(value: string) {
  return new Date(value).toLocaleString();
}

export function getVendorName(item: ProcurementItem) {
  return item.vendorNameSnapshot || item.vendor?.name || UNASSIGNED_VENDOR_LABEL;
}

export function getIngredientCategory(item: ProcurementItem): IngredientCategory {
  return item.ingredient?.category ?? 'OTHER';
}

/** RSVP-independent weekly staple row (see Ingredient.isStaple). */
export function isStapleItem(item: Pick<ProcurementItem, 'sourceType'>) {
  return item.sourceType === 'STAPLE';
}

export const WEEKLY_STAPLES_GROUP_KEY = 'weekly-staples';
export const WEEKLY_STAPLES_GROUP_TITLE = 'Weekly staples';

export function getSourceLabel(item: Pick<ProcurementItem, 'sourceType'>) {
  switch (item.sourceType) {
    case 'MANUAL':
      return 'Manual';
    case 'STAPLE':
      return 'Staple';
    default:
      return 'Recipe';
  }
}

/** Format a quantity for display. Shows the raw value without rounding so
 *  what the user sees matches what the server stores. */
export function formatQuantity(value: string | number | null | undefined) {
  const numericValue = typeof value === 'number' ? value : Number(value);

  if (!Number.isFinite(numericValue)) {
    return value ? String(value) : '';
  }

  // Strip unnecessary trailing zeros but keep the value faithful to the server.
  return String(Math.max(0, numericValue));
}

/** Factor from a breakdown entry's own unit into its line's unit (1 when equal or unknown). */
function getBreakdownEntryMultiplier(entry: Pick<ProcurementBreakdownEntry, 'unit'>, lineUnit: string): number {
  return getAutoConversionMultiplier(entry.unit, lineUnit) ?? 1;
}

export function formatRecipeQuantity(item: Pick<ProcurementItem, 'calculatedQuantity' | 'calculationBreakdown' | 'unit'>) {
  const roundedQuantity = formatQuantity(item.calculatedQuantity);
  if (item.calculationBreakdown.length === 0) {
    return roundedQuantity;
  }

  const preRoundQuantity = item.calculationBreakdown.reduce((total, entry) => {
    const quantity = Number(entry.totalQuantity) * getBreakdownEntryMultiplier(entry, item.unit);
    return Number.isFinite(quantity) ? total + quantity : total;
  }, 0);
  const normalizedPreRoundQuantity = roundProcurementQuantity(preRoundQuantity);

  if (normalizedPreRoundQuantity === Number(item.calculatedQuantity)) {
    return roundedQuantity;
  }

  return `${formatQuantity(normalizedPreRoundQuantity)} -> ${roundedQuantity}`;
}

/** Parse a hand-typed quantity. Kept as typed (to the stored precision), never
 *  rounded up: a caterer who types 1.1 lb means 1.1 lb (issue #140). */
export function parseQuantityInput(value: string) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  return roundProcurementQuantity(numericValue);
}

// ---------------------------------------------------------------------------
// Order increment (issue #140). Purchase quantities are rounded up to a
// multiple of the ingredient's order increment (bag / case / pack size) instead
// of the next whole unit. The increment is stored in the ingredient's default
// unit; a row may carry a different (legacy) unit, so it is converted.
// ---------------------------------------------------------------------------

/**
 * The order increment that applies to a procurement row, in the row's unit.
 * Falls back to 1 (whole units) when the ingredient is not loaded or its
 * default unit cannot be converted into the row unit (e.g. count vs grams).
 */
export function getItemOrderIncrement(item: Pick<ProcurementItem, 'unit' | 'ingredient'>): number {
  const ingredient = item.ingredient;
  if (!ingredient) return 1;
  return convertOrderIncrementToUnit(ingredient.orderIncrement, ingredient.defaultUnit, item.unit);
}

/**
 * "4 × 4 kg" for a purchase that is a whole number of packs; null when the
 * increment is a single unit, nothing is bought, or the value was hand-typed
 * off the pack grid.
 */
export function formatPackCount(purchaseQuantity: string | number, increment: number, unit: string): string | null {
  const purchase = Number(purchaseQuantity);
  if (!Number.isFinite(purchase) || purchase <= 0) return null;
  if (!Number.isFinite(increment) || increment <= 0 || increment === 1) return null;
  const packs = purchase / increment;
  const wholePacks = Math.round(packs);
  if (Math.abs(packs - wholePacks) > 1e-6 || wholePacks <= 0) return null;
  return `${wholePacks} × ${formatQuantity(increment)} ${unit}`;
}

export function createDraft(item: ProcurementItem): ProcurementItemDraft {
  const pq = formatQuantity(item.purchaseQuantity);
  return {
    purchaseQuantity: pq,
    inventoryQuantity: item.inventoryQuantity ? formatQuantity(item.inventoryQuantity) : '',
    notes: item.notes ?? '',
    isExcluded: item.isExcluded,
    autoPurchaseQuantity: pq,
  };
}

export function buildDraftMap(items: ProcurementItem[]): Record<number, ProcurementItemDraft> {
  return Object.fromEntries(items.map((item) => [item.id, createDraft(item)]));
}

export function getServerInventory(item: ProcurementItem): string {
  return item.inventoryQuantity ? formatQuantity(item.inventoryQuantity) : '';
}

/**
 * Merge a row's server response into the draft the user has right now.
 * The draft may have moved on while the request was in flight, so the user's
 * values are KEPT; only fields the server now agrees with are normalized to
 * the server's spelling (e.g. "3.5" saved as 4 -> "4") so the row stops
 * counting as unsaved. Whatever still differs is left for the follow-up save.
 */
export function reconcileDraftAfterSave(draft: ProcurementItemDraft, saved: ProcurementItem): ProcurementItemDraft {
  const serverPurchase = formatQuantity(saved.purchaseQuantity);
  const serverNotes = saved.notes ?? '';
  const purchaseMatches = parseQuantityInput(draft.purchaseQuantity) === Number(saved.purchaseQuantity);
  const notesMatch = draft.notes.trim() === serverNotes;
  const vendorMatches = draft.vendorId === undefined || draft.vendorId === saved.vendorId;

  return {
    purchaseQuantity: purchaseMatches ? serverPurchase : draft.purchaseQuantity,
    // Inventory is saved separately; keep whatever the user has typed there.
    inventoryQuantity: draft.inventoryQuantity,
    notes: notesMatch ? serverNotes : draft.notes,
    isExcluded: draft.isExcluded,
    vendorId: vendorMatches ? undefined : draft.vendorId,
    vendorNameSnapshot: vendorMatches ? undefined : draft.vendorNameSnapshot,
    autoPurchaseQuantity: serverPurchase,
  };
}

export function isDraftChanged(item: ProcurementItem, draft: ProcurementItemDraft) {
  return draft.purchaseQuantity !== formatQuantity(item.purchaseQuantity)
    || draft.inventoryQuantity !== getServerInventory(item)
    || draft.notes !== (item.notes ?? '')
    || draft.isExcluded !== item.isExcluded
    || (draft.vendorId !== undefined && draft.vendorId !== item.vendorId);
}

/** Everything a save of one procurement row needs. An empty object means the
 *  row matches the server. `error` is set instead when the draft cannot be
 *  saved (unparsable quantity); no operations are emitted in that case. */
export type RowSaveOperations = {
  inventoryEntry?: BulkInventoryEntry;
  itemPayload?: UpdateProcurementItemRequest;
  createManual?: AddManualProcurementItemRequest;
  deleteManual?: boolean;
  error?: string;
};

export function hasRowSaveOperations(ops: RowSaveOperations) {
  return Boolean(ops.inventoryEntry || ops.itemPayload || ops.createManual || ops.deleteManual);
}

/**
 * Diff a single row's draft against its server item. Shared by the per-row
 * auto-save and the bulk "Save changes" path so the rules live in one place:
 * - inventory is saved for every non-MANUAL row (RECIPE and STAPLE alike)
 * - local-only rows (negative ids) become a manual-item create
 * - excluded MANUAL rows are deleted; excluded RECIPE/STAPLE rows set isExcluded
 */
export function buildRowSaveOperations(
  item: ProcurementItem,
  draft: ProcurementItemDraft,
  isLocalOnly: boolean,
): RowSaveOperations {
  const ops: RowSaveOperations = {};
  const normalizedNotes = draft.notes.trim() || null;

  if (item.sourceType !== 'MANUAL' && draft.inventoryQuantity !== getServerInventory(item)) {
    ops.inventoryEntry = {
      ingredientId: item.ingredientId,
      quantityOnHand: draft.inventoryQuantity === '' ? null : parseQuantityInput(draft.inventoryQuantity),
    };
  }

  if (isLocalOnly) {
    if (draft.isExcluded) {
      return {};
    }
    const parsedQuantity = parseQuantityInput(draft.purchaseQuantity);
    if (parsedQuantity === null) {
      return { error: `Enter a valid quantity for ${item.displayName}` };
    }
    return {
      createManual: {
        ingredientId: item.ingredientId,
        purchaseQuantity: parsedQuantity,
        vendorId: draft.vendorId,
        notes: normalizedNotes,
      },
    };
  }

  if (item.sourceType === 'MANUAL' && draft.isExcluded) {
    return { deleteManual: true };
  }

  const payload: UpdateProcurementItemRequest = {};

  if (draft.purchaseQuantity !== formatQuantity(item.purchaseQuantity)) {
    const parsedQuantity = parseQuantityInput(draft.purchaseQuantity);
    if (parsedQuantity === null) {
      return { error: `Enter a valid quantity for ${item.displayName}` };
    }
    payload.purchaseQuantity = parsedQuantity;
  }

  if (draft.vendorId !== undefined && draft.vendorId !== item.vendorId) {
    payload.vendorId = draft.vendorId;
  }

  if (draft.notes !== (item.notes ?? '')) {
    payload.notes = normalizedNotes;
  }

  if (item.sourceType !== 'MANUAL' && draft.isExcluded !== item.isExcluded) {
    payload.isExcluded = draft.isExcluded;
  }

  if (Object.keys(payload).length > 0) {
    ops.itemPayload = payload;
  }

  return ops;
}

export function buildGroupedItems(
  items: ProcurementDisplayItem[],
  getKey: (entry: ProcurementDisplayItem) => string,
  getTitle: (entry: ProcurementDisplayItem) => string,
): ProcurementGroup[] {
  const groups = new Map<string, ProcurementGroup>();

  for (const entry of items) {
    const key = getKey(entry);
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(entry);
      continue;
    }

    groups.set(key, {
      key,
      title: getTitle(entry),
      items: [entry],
    });
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      items: [...group.items].sort((a, b) => {
        if (a.draft.isExcluded !== b.draft.isExcluded) {
          return Number(a.draft.isExcluded) - Number(b.draft.isExcluded);
        }
        return a.item.displayName.localeCompare(b.item.displayName);
      }),
    }))
    .sort((a, b) => {
      if (a.key === WEEKLY_STAPLES_GROUP_KEY) return -1;
      if (b.key === WEEKLY_STAPLES_GROUP_KEY) return 1;
      return a.title.localeCompare(b.title);
    });
}

/** Vendor view: one card per vendor, plus a separate "Weekly staples" card listed first. */
export function buildVendorGroups(items: ProcurementDisplayItem[]): ProcurementGroup[] {
  return buildGroupedItems(
    items,
    (entry) => (isStapleItem(entry.item) ? WEEKLY_STAPLES_GROUP_KEY : `${entry.item.vendorId ?? 'none'}-${getVendorName(entry.item)}`),
    (entry) => (isStapleItem(entry.item) ? WEEKLY_STAPLES_GROUP_TITLE : getVendorName(entry.item)),
  );
}

export function buildCategoryGroups(items: ProcurementDisplayItem[]): ProcurementGroup[] {
  return buildGroupedItems(
    items,
    (entry) => (isStapleItem(entry.item) ? WEEKLY_STAPLES_GROUP_KEY : getIngredientCategory(entry.item)),
    (entry) => (isStapleItem(entry.item) ? WEEKLY_STAPLES_GROUP_TITLE : INGREDIENT_CATEGORY_LABELS[getIngredientCategory(entry.item)]),
  );
}

function getDraftVendorName(entry: ProcurementDisplayItem) {
  if (entry.draft.vendorId !== undefined) {
    return entry.draft.vendorNameSnapshot || UNASSIGNED_VENDOR_LABEL;
  }

  return getVendorName(entry.item);
}

function getDraftVendorKey(entry: ProcurementDisplayItem) {
  const vendorId = entry.draft.vendorId !== undefined ? entry.draft.vendorId : entry.item.vendorId;
  return `${vendorId ?? 'none'}:${getDraftVendorName(entry)}`;
}

function getDraftExportQuantity(entry: ProcurementDisplayItem) {
  const quantity = entry.draft.isExcluded ? '0' : formatQuantity(entry.draft.purchaseQuantity);
  return entry.item.unit ? `${quantity} ${entry.item.unit}` : quantity;
}

function isDraftExportRowStruck(entry: ProcurementDisplayItem) {
  return entry.draft.isExcluded || Number(entry.draft.purchaseQuantity) === 0;
}

export function buildDraftProcurementExportGroups(items: ProcurementDisplayItem[]): ProcurementExportGroup[] {
  const groups = new Map<string, { title: string; items: ProcurementDisplayItem[] }>();

  for (const entry of items) {
    const key = getDraftVendorKey(entry);
    const title = getDraftVendorName(entry);
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(entry);
      continue;
    }

    groups.set(key, {
      title,
      items: [entry],
    });
  }

  return [...groups.values()]
    .map((group) => ({
      title: group.title,
      rows: [...group.items].sort((a, b) => {
        const aStruck = isDraftExportRowStruck(a);
        const bStruck = isDraftExportRowStruck(b);
        if (aStruck !== bStruck) {
          return Number(aStruck) - Number(bStruck);
        }
        return a.item.displayName.localeCompare(b.item.displayName);
      }).map((entry) => ({
        ingredient: entry.item.displayName,
        quantity: getDraftExportQuantity(entry),
        notes: entry.draft.notes.trim(),
        strike: isDraftExportRowStruck(entry),
      })),
    }))
    .sort((a, b) => {
      if (a.title === UNASSIGNED_VENDOR_LABEL) return 1;
      if (b.title === UNASSIGNED_VENDOR_LABEL) return -1;
      return a.title.localeCompare(b.title);
    });
}

export async function searchIngredients(query: string): Promise<Ingredient[]> {
  const response = await fetchIngredients({
    search: query,
    activeOnly: true,
    page: 1,
    pageSize: 20,
  });
  return response.ingredients;
}

/** The ingredient whose name equals this one (case-insensitive), deactivated ones included; null when none. */
export async function findIngredientByName(name: string): Promise<Ingredient | null> {
  const wanted = name.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!wanted) return null;
  const response = await fetchIngredients({ search: name.trim(), activeOnly: 'all', page: 1, pageSize: 50 });
  return response.ingredients.find((ingredient) => ingredient.name.trim().toLowerCase().replace(/\s+/g, ' ') === wanted) ?? null;
}

export type InventoryStatus = 'sufficient' | 'partial' | 'none';

export function getInventoryStatus(calculatedQty: string, inventoryQty: string): InventoryStatus {
  const inv = Number(inventoryQty);
  if (!Number.isFinite(inv) || inv <= 0) return 'none';
  const calc = Number(calculatedQty);
  if (inv >= calc) return 'sufficient';
  return 'partial';
}

/** RECIPE purchase = need minus on hand, rounded up to the order increment (default: whole units). */
export function computeSuggestedPurchase(calculatedQty: string, inventoryQty: string, increment = 1): number {
  const calc = Number(calculatedQty);
  const inv = Number(inventoryQty);
  if (!Number.isFinite(calc) || !Number.isFinite(inv)) return roundUpToIncrement(calc || 0, increment);
  return roundUpToIncrement(calc - inv, increment);
}

// ---------------------------------------------------------------------------
// Shared on-hand. Inventory is stored once per (list, ingredient), but the UI
// shows an On Hand input on every non-MANUAL row, so a RECIPE row and a STAPLE
// row of the same ingredient share one value. Recipe rows consume the stock
// first; the staple only counts what is left (mirrors the server rule).
// ---------------------------------------------------------------------------

/** Rows that share an ingredient's on-hand record (RECIPE and STAPLE). */
function getSharedInventoryRows(items: ProcurementItem[], ingredientId: number): ProcurementItem[] {
  return items.filter((item) => item.ingredientId === ingredientId && item.sourceType !== 'MANUAL');
}

/** The unit the on-hand was saved in, unless no row can convert from it any more (stale after a unit change). */
function getUsableSavedInventoryUnit(rows: ProcurementItem[]): string | undefined {
  const saved = rows.find((item) => item.inventoryUnit)?.inventoryUnit;
  if (!saved) return undefined;
  return rows.some((row) => getAutoConversionMultiplier(saved, row.unit) !== null) ? saved : undefined;
}

/**
 * Unit an ingredient's shared on-hand is tracked in: the unit it was saved in
 * (while a row can still convert from it), else the STAPLE row's unit, else (when
 * the ingredient is loaded) the default unit if some line can net it, else the
 * first line's unit (resolveSharedInventoryUnit): the unit the next save writes.
 */
export function getSharedInventoryUnit(items: ProcurementItem[], ingredientId: number): string | undefined {
  const rows = getSharedInventoryRows(items, ingredientId);
  const saved = rows.find((item) => item.inventoryUnit)?.inventoryUnit ?? null;
  const base = rows.find((item) => item.sourceType === 'STAPLE')?.unit
    ?? rows.find((item) => item.ingredient?.defaultUnit)?.ingredient?.defaultUnit;
  if (base === undefined) return getUsableSavedInventoryUnit(rows);
  return resolveInventoryUnitOnSave(saved, base, rows.map((item) => item.unit));
}

/**
 * Sum of this week's RECIPE need for an ingredient, expressed in `targetUnit`
 * (default: the unit its staple target / on-hand are tracked in; see
 * getSharedInventoryUnit). Recipe rows keep their own unit, so each is
 * converted; a row whose unit cannot be auto-converted (e.g. count vs grams)
 * is left out of the netting — the row already carries the unit-mismatch
 * warning. Returns 0 when the ingredient has no recipe rows.
 */
export function getRecipeNeedForIngredient(items: ProcurementItem[], ingredientId: number, targetUnit?: string): number {
  const unit = targetUnit ?? getSharedInventoryUnit(items, ingredientId);
  return items.reduce((total, item) => {
    if (item.sourceType !== 'RECIPE' || item.ingredientId !== ingredientId) return total;
    const quantity = Number(item.calculatedQuantity);
    if (!Number.isFinite(quantity)) return total;
    const multiplier = unit === undefined ? 1 : getAutoConversionMultiplier(item.unit, unit);
    return multiplier === null ? total : total + quantity * multiplier;
  }, 0);
}

/** STAPLE purchase = target - max(0, onHand - recipeNeed), rounded up to the order increment. */
export function computeStaplePurchase(calculatedQty: string, inventoryQty: string, recipeNeed: number, increment = 1): number {
  return computeSuggestedPurchase(calculatedQty, String(netStapleOnHand(Number(inventoryQty) || 0, recipeNeed)), increment);
}

/** Whether an on-hand value is the stored one (read in its saved unit) or freshly typed (read in the labelled unit). */
export type SharedInventorySource = { typed?: boolean };

/**
 * How a row relates to the ingredient's shared on-hand: the unit it is tracked in
 * and the factor into the row's unit. null = the row cannot net it because another
 * line of the ingredient does; 0 = the stored value's unit converts to no line any
 * more, so nothing is netted until the on-hand is saved again (mirrors the server);
 * a value the caterer types is read in the labelled unit instead; a lone
 * unconvertible line keeps netting raw (legacy).
 */
function describeSharedInventory(items: ProcurementItem[], item: ProcurementItem, { typed = false }: SharedInventorySource = {}): { trackedUnit?: string; multiplier: number | null } {
  const rows = getSharedInventoryRows(items, item.ingredientId);
  const trackedUnit = getSharedInventoryUnit(items, item.ingredientId);
  const stale = rows.some((row) => row.inventoryUnit) && getUsableSavedInventoryUnit(rows) === undefined;
  if (stale && !typed) return { trackedUnit, multiplier: 0 };
  if (trackedUnit === undefined) return { trackedUnit, multiplier: 1 };
  const multiplier = getAutoConversionMultiplier(trackedUnit, item.unit);
  if (multiplier !== null) return { trackedUnit, multiplier };
  const siblingNets = rows.some((other) => other.id !== item.id && getAutoConversionMultiplier(trackedUnit, other.unit) !== null);
  return { trackedUnit, multiplier: siblingNets ? null : 1 };
}

/**
 * The saved on-hand of a row whose stored unit no line can convert any more, so the
 * caterer can see it and re-enter the amount in the new unit; undefined otherwise.
 */
export function getStaleInventory(items: ProcurementItem[], item: ProcurementItem): { quantity: string; unit: string } | undefined {
  if (item.sourceType === 'MANUAL') return undefined;
  const rows = getSharedInventoryRows(items, item.ingredientId);
  const saved = rows.find((row) => row.inventoryUnit && row.inventoryQuantity);
  if (!saved || getUsableSavedInventoryUnit(rows) !== undefined) return undefined;
  return { quantity: formatQuantity(saved.inventoryQuantity), unit: saved.inventoryUnit! };
}

/**
 * Rows with a stale saved on-hand are shown without it: the On Hand box starts
 * empty, so whatever the caterer types (even the same number) is a change and is
 * saved in the unit the list now tracks. `context` is the full generated list.
 */
export function maskStaleInventory(items: ProcurementItem[], context: ProcurementItem[] = items): ProcurementItem[] {
  return items.map((item) => (getStaleInventory(context, item) ? { ...item, inventoryQuantity: null } : item));
}

/** Shared on-hand expressed in a row's unit; null when the row cannot net it (mirrors the server). */
export function convertSharedInventoryToLineUnit(items: ProcurementItem[], item: ProcurementItem, inventoryValue: string, source: SharedInventorySource = {}): string | null {
  const { multiplier } = describeSharedInventory(items, item, source);
  if (multiplier === null) return null;
  const value = Number(inventoryValue);
  if (multiplier === 1 || inventoryValue.trim() === '' || !Number.isFinite(value)) return inventoryValue;
  if (multiplier === 0) return '0';
  return String(roundProcurementQuantity(value * multiplier));
}

/** The unit on-hand is tracked in when this row cannot net it; undefined when the row nets normally. */
export function getInventoryUnitMismatch(items: ProcurementItem[], item: ProcurementItem): string | undefined {
  if (item.sourceType === 'MANUAL') return undefined;
  const { trackedUnit, multiplier } = describeSharedInventory(items, item);
  return multiplier === null ? trackedUnit : undefined;
}

/** Ids of every row that shares the ingredient's inventory record (RECIPE and STAPLE). */
export function getSharedInventoryRowIds(items: ProcurementItem[], ingredientId: number): number[] {
  return items
    .filter((item) => item.sourceType !== 'MANUAL' && item.ingredientId === ingredientId)
    .map((item) => item.id);
}

/**
 * Apply one on-hand value to every row sharing the ingredient and recompute
 * each row's suggested purchase. Returns the same reference if nothing changed.
 */
export function applySharedInventoryDraft(
  drafts: Record<number, ProcurementItemDraft>,
  items: ProcurementItem[],
  ingredientId: number,
  inventoryValue: string,
  /** Lines that decide netting (the generated list); defaults to `items`. */
  nettingItems: ProcurementItem[] = items,
): Record<number, ProcurementItemDraft> {
  let changed = false;
  const next = { ...drafts };
  for (const item of items) {
    if (item.sourceType === 'MANUAL' || item.ingredientId !== ingredientId) continue;
    const existing = drafts[item.id];
    if (!existing) continue;
    const increment = getItemOrderIncrement(item);
    let purchase: number | null;
    if (item.sourceType === 'STAPLE') {
      // null = a sibling line owns the on-hand; the staple nets nothing, like the server.
      const onHand = convertSharedInventoryToLineUnit(nettingItems, item, inventoryValue, { typed: true }) ?? '0';
      purchase = computeStaplePurchase(item.calculatedQuantity, onHand, getRecipeNeedForIngredient(items, ingredientId, item.unit), increment);
    } else {
      const inventoryInUnit = convertSharedInventoryToLineUnit(nettingItems, item, inventoryValue, { typed: true });
      purchase = inventoryInUnit === null ? null : computeSuggestedPurchase(item.calculatedQuantity, inventoryInUnit, increment);
    }
    // A row that cannot net the on-hand still mirrors it so it never reports an inventory change.
    next[item.id] = purchase === null
      ? { ...existing, inventoryQuantity: inventoryValue }
      : { ...existing, inventoryQuantity: inventoryValue, purchaseQuantity: String(purchase), autoPurchaseQuantity: String(purchase) };
    changed = true;
  }
  return changed ? next : drafts;
}

export function roundProcurementQuantity(value: number): number {
  return roundQuantity(value);
}

export function getBreakdownMediumToLargeRatio(entry: ProcurementBreakdownEntry): number {
  const rawRatio = Number(entry.mediumToLargeRatio ?? MEDIUM_TO_LARGE_RATIO);
  return Number.isFinite(rawRatio) && rawRatio > 0 ? rawRatio : MEDIUM_TO_LARGE_RATIO;
}

function readSizeCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

export function getBreakdownSizeCounts(entry: ProcurementBreakdownEntry): RSVPSizeCounts {
  return createRsvpSizeCounts({
    S: readSizeCount(entry.sizeCounts?.S ?? entry.smallCount),
    M: readSizeCount(entry.sizeCounts?.M ?? entry.mediumCount),
    L: readSizeCount(entry.sizeCounts?.L ?? entry.largeCount),
  });
}

export function getBreakdownSizeOptOutCounts(entry: ProcurementBreakdownEntry): RSVPSizeCounts {
  return createRsvpSizeCounts({
    S: readSizeCount(entry.sizeOptOutCounts?.S ?? entry.smallOptOutCount),
    M: readSizeCount(entry.sizeOptOutCounts?.M ?? entry.mediumOptOutCount),
    L: readSizeCount(entry.sizeOptOutCounts?.L ?? entry.largeOptOutCount),
  });
}

export function getBreakdownScaleRatios(entry: ProcurementBreakdownEntry): RSVPScaleRatios {
  return {
    S: readScaleRatio(entry.sizeScaleRatios?.S, DEFAULT_RSVP_SIZE_SCALE_RATIOS.S),
    M: readScaleRatio(entry.sizeScaleRatios?.M ?? entry.mediumToLargeRatio, DEFAULT_RSVP_SIZE_SCALE_RATIOS.M),
    L: readScaleRatio(entry.sizeScaleRatios?.L, DEFAULT_RSVP_SIZE_SCALE_RATIOS.L),
  };
}

function readScaleRatio(value: unknown, fallback: number): number {
  const parsed = Number(value ?? fallback);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export function calculateBreakdownQuantity(
  quantityPerLarge: number,
  counts: RSVPSizeCounts,
  ratios: RSVPScaleRatios
): number {
  return RSVP_SIZES.reduce(
    (total, size) => total + (counts[size] * quantityPerLarge * ratios[size]),
    0
  );
}

export function getProcurementRowState(entry: ProcurementDisplayItem, viewMode: ProcurementViewMode) {
  const { item, draft } = entry;
  const originalQuantity = formatQuantity(item.purchaseQuantity);
  const quantityChanged = draft.purchaseQuantity !== originalQuantity;
  const inventoryChanged = draft.inventoryQuantity !== getServerInventory(item);
  const notesChanged = draft.notes !== (item.notes ?? '');
  const exclusionChanged = draft.isExcluded !== item.isExcluded;
  const vendorChanged = draft.vendorId !== undefined && draft.vendorId !== item.vendorId;
  const isNewItem = entry.isLocalOnly;
  const hasUnsavedChange = isNewItem || quantityChanged || inventoryChanged || notesChanged || exclusionChanged || vendorChanged;
  const contextLabel = viewMode === 'vendor'
    ? INGREDIENT_CATEGORY_LABELS[getIngredientCategory(item)]
    : getVendorName(item);

  const isManual = item.sourceType === 'MANUAL';
  const isStaple = isStapleItem(item);
  const sourceLabel = getSourceLabel(item);
  const inventoryStatus: InventoryStatus = isManual
    ? 'none'
    : getInventoryStatus(item.calculatedQuantity, entry.inventoryInLineUnit ?? draft.inventoryQuantity);
  const isZeroNet = !isManual
    && !draft.isExcluded
    && inventoryStatus === 'sufficient'
    && Number(draft.purchaseQuantity) === 0;

  let rowHighlightClass = '';
  if (draft.isExcluded) {
    rowHighlightClass = exclusionChanged || isNewItem ? 'bg-rose-50/80' : 'bg-muted/30 opacity-60';
  } else if (isZeroNet) {
    rowHighlightClass = 'bg-muted/30 opacity-60';
  } else if (isNewItem) {
    rowHighlightClass = 'bg-emerald-50/80';
  } else if (hasUnsavedChange) {
    rowHighlightClass = 'bg-amber-50/80';
  }

  const editableInputClass = (fieldChanged: boolean) => [
    draft.isExcluded ? 'line-through' : '',
    isNewItem
      ? 'border-emerald-200 bg-emerald-50'
      : exclusionChanged && draft.isExcluded
        ? 'border-rose-200 bg-rose-50'
        : fieldChanged
          ? 'border-amber-200 bg-amber-50'
          : '',
  ].filter(Boolean).join(' ');

  const quantityInputClass = editableInputClass(quantityChanged);
  const inventoryInputClass = [
    inventoryStatus === 'sufficient'
      ? 'border-emerald-200 bg-emerald-50'
      : inventoryStatus === 'partial'
        ? 'border-amber-200 bg-amber-50'
        : inventoryChanged
          ? 'border-amber-200 bg-amber-50'
          : '',
  ].filter(Boolean).join(' ');
  const notesInputClass = editableInputClass(notesChanged);
  const actionClass = exclusionChanged
    ? 'text-rose-700 hover:bg-rose-100'
    : isNewItem
      ? 'text-emerald-700 hover:bg-emerald-100'
      : hasUnsavedChange
        ? 'text-amber-700 hover:bg-amber-100'
        : '';

  const vendorSelectClass = vendorChanged
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : '';

  return {
    item,
    draft,
    isNewItem,
    isManual,
    isStaple,
    sourceLabel,
    hasUnsavedChange,
    contextLabel,
    inventoryStatus,
    isZeroNet,
    vendorChanged,
    rowHighlightClass,
    quantityInputClass,
    inventoryInputClass,
    notesInputClass,
    actionClass,
    vendorSelectClass,
  };
}

// ---------------------------------------------------------------------------
// RSVP Override Feature (temporary) — simulation helper
// Remove this function when the RSVP Override Feature is retired.
// ---------------------------------------------------------------------------

// Simulation overrides are authoritative assumed counts. When an override is
// present, missing sizes intentionally normalize to 0 instead of falling back
// to the generated snapshot counts.
export type RsvpOverrideMap = Record<number, Partial<RSVPSizeCounts> & { mediumCount?: number; largeCount?: number }>;

function getOverrideSizeCounts(
  override: RsvpOverrideMap[number] | undefined,
  snapshotCounts: RSVPSizeCounts
): RSVPSizeCounts {
  if (!override) return snapshotCounts;
  return createRsvpSizeCounts({
    S: readSizeCount(override.S),
    M: readSizeCount(override.M ?? override.mediumCount),
    L: readSizeCount(override.L ?? override.largeCount),
  });
}

function subtractSizeCounts(counts: RSVPSizeCounts, subtract: RSVPSizeCounts): RSVPSizeCounts {
  return createRsvpSizeCounts(Object.fromEntries(
    RSVP_SIZES.map((size) => [size, Math.max(0, counts[size] - subtract[size])])
  ) as Record<RSVPSizeCode, number>);
}

/**
 * Simulate procurement items with overridden RSVP counts.
 * Mirrors server generation logic: skips zero-total breakdowns, drops items
 * with no remaining entries, and recomputes purchaseQuantity as
 * (calculatedQuantity - inventoryOnHand) rounded up to the ingredient's order
 * increment (whole units when the increment is 1). STAPLE rows are RSVP-independent
 * but net the (simulated) recipe need of their ingredient against the shared
 * on-hand, so their purchaseQuantity is re-derived afterwards.
 */
export function simulateProcurementItems(
  items: ProcurementItem[],
  rsvpOverrides: RsvpOverrideMap,
): ProcurementItem[] {
  const simulated = simulateRecipeItems(items, rsvpOverrides);
  return simulated.map((item) => {
    if (!isStapleItem(item)) return item;
    const recipeNeed = getRecipeNeedForIngredient(simulated, item.ingredientId, item.unit);
    const onHand = convertSharedInventoryToLineUnit(items, item, item.inventoryQuantity ?? '') ?? '0';
    const purchaseQuantity = String(computeStaplePurchase(item.calculatedQuantity, onHand, recipeNeed, getItemOrderIncrement(item)));
    return purchaseQuantity === item.purchaseQuantity ? item : { ...item, purchaseQuantity };
  });
}

function simulateRecipeItems(
  items: ProcurementItem[],
  rsvpOverrides: RsvpOverrideMap,
): ProcurementItem[] {
  return items.map((item) => {
    if (item.sourceType !== 'RECIPE' || item.calculationBreakdown.length === 0) {
      return item;
    }

    let simulatedCalculated = 0;
    const nextBreakdown = item.calculationBreakdown.reduce<ProcurementBreakdownEntry[]>((acc, entry) => {
      const quantityPerLarge = Number(entry.quantityPerLarge);
      if (!Number.isFinite(quantityPerLarge)) return acc;

      const entryCounts = getBreakdownSizeCounts(entry);
      const override = rsvpOverrides[entry.menuId];
      let sizeCounts = getOverrideSizeCounts(override, entryCounts);

      if (entry.isOptional && override) {
        sizeCounts = subtractSizeCounts(sizeCounts, getBreakdownSizeOptOutCounts(entry));
      }

      const scaleRatios = getBreakdownScaleRatios(entry);
      const totalQuantity = roundProcurementQuantity(
        calculateBreakdownQuantity(quantityPerLarge, sizeCounts, scaleRatios)
      );
      if (totalQuantity <= 0) return acc;

      simulatedCalculated += totalQuantity * getBreakdownEntryMultiplier(entry, item.unit);
      acc.push({
        ...entry,
        sizeCounts,
        sizeScaleRatios: entry.sizeScaleRatios ?? scaleRatios,
        mediumToLargeRatio: entry.mediumToLargeRatio ?? String(MEDIUM_TO_LARGE_RATIO),
        smallCount: sizeCounts.S,
        mediumCount: sizeCounts.M,
        largeCount: sizeCounts.L,
        totalQuantity: totalQuantity.toFixed(QUANTITY_DECIMAL_PLACES),
      });
      return acc;
    }, []);

    if (nextBreakdown.length === 0) return null;

    // The need stays exact; only the purchase is rounded, to the order increment.
    const exactCalculated = roundProcurementQuantity(simulatedCalculated);
    const purchaseQuantity = String(
      computeSuggestedPurchase(String(exactCalculated), convertSharedInventoryToLineUnit(items, item, item.inventoryQuantity ?? '') ?? '', getItemOrderIncrement(item))
    );

    return {
      ...item,
      calculatedQuantity: String(exactCalculated),
      purchaseQuantity,
      calculationBreakdown: nextBreakdown,
    };
  }).filter((item): item is ProcurementItem => item !== null);
}

/**
 * Reset auto-synced draft purchaseQuantity values back to server baseline.
 * Only resets items where the user hasn't manually edited (purchase matches auto).
 * Returns the same reference if nothing changed.
 */
export function resetAutoSyncedDrafts(
  drafts: Record<number, ProcurementItemDraft>,
  serverItems: ProcurementItem[],
): Record<number, ProcurementItemDraft> {
  let changed = false;
  const next = { ...drafts };
  for (const serverItem of serverItems) {
    const draft = drafts[serverItem.id];
    if (!draft?.autoPurchaseQuantity) continue;
    const serverValue = formatQuantity(serverItem.purchaseQuantity);
    if (draft.purchaseQuantity === draft.autoPurchaseQuantity && draft.purchaseQuantity !== serverValue) {
      next[serverItem.id] = { ...draft, purchaseQuantity: serverValue, autoPurchaseQuantity: serverValue };
      changed = true;
    }
  }
  return changed ? next : drafts;
}
